<template>
  <div class="statistiche-view">
    <div class="header-nav">
      <button class="btn-back" @click="home">
        <i class="fas fa-arrow-left"></i> {{ $t('home') }}
      </button>
      <h1 class="title">{{ $t('statistiche') }}</h1>
    </div>

    <div class="controls-panel card-glass mb-4 d-flex align-items-start flex-wrap gap-4">
      <div class="control-item">
        <label class="label-glass">
          <i class="fas fa-calendar-alt text-primary me-1"></i> Data Riferimento:
        </label>
        <v-date-picker v-model="referenceDate" is-dark mode="date" />
      </div>

      <div class="control-item">
        <label class="label-glass">
          <i class="fas fa-chart-line text-info me-1"></i> Periodo Report:
        </label>
        <div class="filter-pills">
          <button class="btn-pill" :class="{ active: period === 'giorno' }" @click="setPeriod('giorno')">Giorno</button>
          <button class="btn-pill" :class="{ active: period === 'settimana' }" @click="setPeriod('settimana')">Settimana</button>
          <button class="btn-pill" :class="{ active: period === 'mese' }" @click="setPeriod('mese')">Mese</button>
          <button class="btn-pill" :class="{ active: period === 'anno' }" @click="setPeriod('anno')">Anno</button>
        </div>
        <div class="mt-2 text-muted small font-weight-bold" style="min-height: 18px; font-size: 0.78rem;">
          {{ currentPeriodLabel }}
        </div>
      </div>

      <div class="control-item">
        <label class="label-glass">
          <i class="fas fa-truck text-warning me-1"></i> Delivery:
        </label>
        <div class="filter-pills">
          <button class="btn-pill" :class="{ active: filterDelivery === 'tutti' }" @click="filterDelivery = 'tutti'">
            Tutti
          </button>
          <button class="btn-pill" :class="{ active: filterDelivery === 'nessuno' }" @click="filterDelivery = 'nessuno'">
            <i class="fas fa-store me-1"></i> Ristorante
          </button>
          <button 
            v-for="plat in deliveryPlatforms" 
            :key="plat.nome" 
            class="btn-pill" 
            :class="{ active: filterDelivery === plat.nome }" 
            @click="filterDelivery = plat.nome"
          >
            {{ plat.nome }}
          </button>
        </div>
      </div>

      <div class="control-item">
        <label class="label-glass">
          <i class="fas fa-wallet text-success me-1"></i> Pagamento:
        </label>
        <div class="filter-pills">
          <button class="btn-pill" :class="{ active: filterPayment === 'tutti' }" @click="filterPayment = 'tutti'">
            Tutti
          </button>
          <button class="btn-pill" :class="{ active: filterPayment === 'contante' }" @click="filterPayment = 'contante'">
            <i class="fas fa-money-bill-wave me-1"></i> Contanti
          </button>
          <button class="btn-pill" :class="{ active: filterPayment === 'digitale' }" @click="filterPayment = 'digitale'">
            <i class="fas fa-credit-card me-1"></i> Digitale
          </button>
        </div>
      </div>

      <button class="btn-refresh ms-auto align-self-center mt-3 mt-md-0" @click="fetchData" :disabled="loading">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i> Aggiorna
      </button>
    </div>

    <!-- Summary Revenue Cards -->
    <div class="row mb-4">
      <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
        <div class="card-glass summary-card text-center py-4">
          <div class="summary-label text-muted small uppercase font-weight-bold mb-2">Incasso Totale</div>
          <div class="summary-value text-success font-weight-bold h2 m-0">€ {{ totalRevenue.toFixed(2) }}</div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
        <div class="card-glass summary-card text-center py-4">
          <div class="summary-label text-muted small uppercase font-weight-bold mb-2">Incasso Tavoli</div>
          <div class="summary-value text-primary font-weight-bold h2 m-0">€ {{ totalTableRevenue.toFixed(2) }}</div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6 mb-3 mb-md-0">
        <div class="card-glass summary-card text-center py-4">
          <div class="summary-label text-muted small uppercase font-weight-bold mb-2">Incasso Asporto</div>
          <div class="summary-value text-info font-weight-bold h2 m-0">€ {{ totalAsportoRevenue.toFixed(2) }}</div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6">
        <div class="card-glass summary-card text-center py-4">
          <div class="summary-label text-muted small uppercase font-weight-bold mb-2">Incasso Delivery</div>
          <div class="summary-value text-warning font-weight-bold h2 m-0">€ {{ totalDeliveryRevenue.toFixed(2) }}</div>
        </div>
      </div>
    </div>

    <div class="dashboard-grid">
      <!-- Top Dishes Card (Classifica dal 1^ a scendere) -->
      <div class="stats-card card-glass">
        <h3 class="chart-title"><i class="fas fa-trophy text-warning me-2"></i> Piatti più Venduti</h3>
        <div class="stats-list mt-3">
          <div v-for="(item, index) in topDishes" :key="item.piatto" class="stats-list-item d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center" style="min-width: 0;">
              <span :class="['rank-badge me-2', 'rank-' + (index + 1)]">#{{ index + 1 }}</span>
              <span class="product-name font-weight-bold text-truncate" style="max-width: 250px;">{{ item.piatto }}</span>
            </div>
            <span class="product-qty font-weight-bold text-success">{{ item.quantita }} venduti</span>
          </div>
          <div v-if="!topDishes.length && !loading" class="no-data">Nessun dato disponibile</div>
        </div>
      </div>

      <!-- Waiter Performance Table -->
      <div class="stats-card card-glass">
        <h3 class="chart-title"><i class="fas fa-users text-primary me-2"></i> Statistiche Camerieri</h3>
        <div class="table-responsive" style="max-height: 480px; overflow-y: auto;">
          <table class="table-glass">
            <thead>
              <tr>
                <th>Cameriere</th>
                <th class="text-center">Scontrino Medio</th>
                <th class="text-center">Dolci</th>
                <th class="text-center">Vini</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="waiter in waiterStats" :key="waiter.cameriere">
                <td>{{ waiter.cameriere }}</td>
                <td class="text-center">€ {{ waiter.avgReceipt.toFixed(2) }}</td>
                <td class="text-center">{{ waiter.dolci }}</td>
                <td class="text-center">{{ waiter.vino }}</td>
              </tr>
              <tr v-if="!waiterStats.length && !loading">
                <td colspan="4" class="text-center py-4">Nessun dato disponibile</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Busiest Days -->
      <div class="stats-card card-glass">
        <h3 class="chart-title"><i class="fas fa-calendar-day text-info me-2"></i> Giorni con Più Lavoro</h3>
        <div class="stats-list mt-3">
          <div v-for="day in busiestDays" :key="day.name" class="stats-list-item mb-3">
            <div class="d-flex justify-content-between mb-1">
              <span class="day-name font-weight-bold">{{ day.name }}</span>
              <span class="day-stats text-muted small">{{ day.count }} ordini (€ {{ day.revenue.toFixed(2) }})</span>
            </div>
            <div class="progress progress-stats">
              <div class="progress-bar bg-info" role="progressbar" :style="{ width: day.percentage + '%' }" :aria-valuenow="day.percentage" aria-valuemin="0" aria-valuemax="100"></div>
            </div>
          </div>
          <div v-if="!busiestDays.some(d => d.count > 0) && !loading" class="no-data">Nessun dato disponibile</div>
        </div>
      </div>

      <!-- Busiest Hours -->
      <div class="stats-card card-glass">
        <h3 class="chart-title"><i class="fas fa-clock text-success me-2"></i> Orari di Maggior Lavoro</h3>
        <div class="stats-list mt-3">
          <div v-for="hour in busiestHours" :key="hour.name" class="stats-list-item mb-3">
            <div class="d-flex justify-content-between mb-1">
              <span class="hour-name font-weight-bold">{{ hour.name }}</span>
              <span class="hour-stats text-muted small">{{ hour.count }} ordini</span>
            </div>
            <div class="progress progress-stats">
              <div class="progress-bar bg-success" role="progressbar" :style="{ width: hour.percentage + '%' }" :aria-valuenow="hour.percentage" aria-valuemin="0" aria-valuemax="100"></div>
            </div>
          </div>
          <div v-if="!busiestHours.some(h => h.count > 0) && !loading" class="no-data">Nessun dato disponibile</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../store/store.js'
import moment from 'moment'

export default {
  name: 'Statistiche',
  data() {
    return {
      store,
      loading: false,
      referenceDate: new Date(),
      period: 'mese',
      topDishes: [],
      waiterStats: [],
      busiestDays: [],
      busiestHours: [],
      totalRevenue: 0,
      totalTableRevenue: 0,
      totalAsportoRevenue: 0,
      totalDeliveryRevenue: 0,
      filterDelivery: 'tutti',
      filterPayment: 'tutti',
      deliveryPlatforms: []
    }
  },
  computed: {
    currentPeriodLabel() {
      const mDate = moment(this.referenceDate);
      if (this.period === 'giorno') {
        return mDate.format('DD MMMM YYYY');
      } else if (this.period === 'settimana') {
        const start = mDate.clone().startOf('isoWeek').format('DD/MM/YYYY');
        const end = mDate.clone().endOf('isoWeek').format('DD/MM/YYYY');
        return `Settimana: dal ${start} al ${end}`;
      } else if (this.period === 'mese') {
        return `Mese: ${mDate.format('MMMM YYYY')}`;
      } else if (this.period === 'anno') {
        return `Anno: ${mDate.format('YYYY')}`;
      }
      return '';
    }
  },
  methods: {
    home() {
      this.$router.push({ name: 'Gestionale' })
    },
    setPeriod(p) {
      this.period = p;
    },
    async fetchData() {
      this.loading = true
      try {
        const mDate = moment(this.referenceDate)
        let start, end
        
        if (this.period === 'settimana') {
          start = mDate.clone().startOf('isoWeek').unix()
          end = mDate.clone().endOf('isoWeek').unix()
        } else if (this.period === 'mese') {
          start = mDate.clone().startOf('month').unix()
          end = mDate.clone().endOf('month').unix()
        } else if (this.period === 'anno') {
          start = mDate.clone().startOf('year').unix()
          end = mDate.clone().endOf('year').unix()
        } else { // giorno
          start = mDate.clone().startOf('day').unix()
          end = mDate.clone().endOf('day').unix()
        }

        const response = await this.axios.post(this.store.path + 'statistiche/getStatistiche', {
          start,
          end,
          filterDelivery: this.filterDelivery,
          filterPayment: this.filterPayment
        }, {
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        })
        
        if (response.data) {
          this.topDishes = response.data.topDishes
          this.waiterStats = response.data.waiterStats
          this.busiestDays = response.data.busiestDays || []
          this.busiestHours = response.data.busiestHours || []
          this.totalRevenue = response.data.totalRevenue || 0
          this.totalTableRevenue = response.data.totalTableRevenue || 0
          this.totalAsportoRevenue = response.data.totalAsportoRevenue || 0
          this.totalDeliveryRevenue = response.data.totalDeliveryRevenue || 0
          this.deliveryPlatforms = response.data.deliveryPlatforms || []
        }
      } catch (error) {
        console.error('Errore durante il recupero delle statistiche:', error)
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    referenceDate() {
      this.fetchData();
    },
    period() {
      this.fetchData();
    },
    filterDelivery() {
      this.fetchData();
    },
    filterPayment() {
      this.fetchData();
    }
  },
  mounted() {
    this.fetchData()
  }
}
</script>

<style scoped>
.statistiche-view {
  padding: 30px;
  background: white;
  min-height: 100vh;
  color: #333;
}

.header-nav {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
}

.btn-back {
  background: #f0f0f0;
  border: 1px solid #ddd;
  color: #333;
  padding: 8px 15px;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 20px;
}

.title {
  margin: 0;
  font-weight: 700;
  font-size: 1.8rem;
}

.card-glass {
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.controls-panel {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 24px;
}

.control-item {
  display: flex;
  flex-direction: column;
}

.filter-pills {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  background: rgba(0, 0, 0, 0.3);
  padding: 4px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.btn-pill {
  background: transparent;
  border: none;
  color: #a0a0c0;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
}

.btn-pill:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.btn-pill.active {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}

.input-range {
  display: flex;
  align-items: center;
  gap: 10px;
}

.input-glass {
  background: white;
  border: 1px solid #ddd;
  color: #333;
  padding: 10px;
  border-radius: 10px;
  outline: none;
}

.label-glass {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #666;
}

.btn-refresh {
  background: #42b983;
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh:hover:not(:disabled) {
  background: #369a6d;
  transform: translateY(-2px);
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.chart-title {
  font-size: 1.2rem;
  margin-bottom: 20px;
  color: #42b983;
  border-left: 4px solid #42b983;
  padding-left: 15px;
}

.chart-container {
  height: 350px;
  position: relative;
}

.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #999;
}

.table-glass {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
}

.table-glass th {
  color: #666;
  text-transform: uppercase;
  font-size: 0.8rem;
  padding: 10px;
}

.table-glass tr {
  background: #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

.table-glass td {
  padding: 15px;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  color: #2c3e50;
}

.table-glass tr td:first-child {
  border-left: 1px solid #f0f0f0;
  border-radius: 12px 0 0 12px;
}

.table-glass tr td:last-child {
  border-right: 1px solid #f0f0f0;
  border-radius: 0 12px 12px 0;
}

@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

/* Custom dashboard list and rank badge styles */
.stats-list {
  max-height: 480px;
  overflow-y: auto;
  padding-right: 5px;
}

.stats-list::-webkit-scrollbar {
  width: 5px;
}
.stats-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.stats-list-item {
  background: #fdfdfd;
  padding: 12px 15px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 4px rgba(0,0,0,0.01);
  color: #2c3e50;
}

.product-name {
  color: #2c3e50;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-weight: bold;
  font-size: 0.85rem;
  color: white;
  background: #7f8c8d;
}

.rank-1 {
  background: linear-gradient(135deg, #f1c40f 0%, #f39c12 100%);
  box-shadow: 0 3px 6px rgba(241, 196, 15, 0.3);
}

.rank-2 {
  background: linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%);
  box-shadow: 0 3px 6px rgba(189, 195, 199, 0.3);
}

.rank-3 {
  background: linear-gradient(135deg, #e67e22 0%, #d35400 100%);
  box-shadow: 0 3px 6px rgba(230, 126, 34, 0.3);
}

.progress-stats {
  height: 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-top: 5px;
}

.stats-card {
  height: 100%;
}

.summary-card {
  transition: transform 0.2s ease;
}

.summary-card:hover {
  transform: translateY(-3px);
}
</style>
